/** Shared value semantics for the config, pipeline and diagnostic presentation paths. */
export function isInsightRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function cloneInsightValue<T>(value: T): T {
  return cloneValue(value, new WeakMap()) as T
}

function cloneValue(value: unknown, seen: WeakMap<object, unknown>): unknown {
  if (typeof value !== 'object' || value === null) return value
  const existing = seen.get(value)
  if (existing !== undefined) return existing
  if (value instanceof Date) return new Date(value.getTime())
  if (Array.isArray(value)) {
    const next: unknown[] = []
    seen.set(value, next)
    for (const item of value) next.push(cloneValue(item, seen))
    return next
  }
  if (!isInsightRecord(value)) return value
  const next: Record<string, unknown> = {}
  seen.set(value, next)
  for (const key of Object.keys(value)) next[key] = cloneValue(value[key], seen)
  return next
}

/** Key order does not change the meaning of JSON-shaped insight configuration. */
export function insightValuesEqual(first: unknown, second: unknown): boolean {
  if (Object.is(first, second)) return true
  if (first instanceof Date || second instanceof Date) {
    return first instanceof Date && second instanceof Date && first.getTime() === second.getTime()
  }
  if (Array.isArray(first) || Array.isArray(second)) {
    return (
      Array.isArray(first) &&
      Array.isArray(second) &&
      first.length === second.length &&
      first.every((value, index) => insightValuesEqual(value, second[index]))
    )
  }
  if (!isInsightRecord(first) || !isInsightRecord(second)) return false
  const firstKeys = Object.keys(first).sort()
  const secondKeys = Object.keys(second).sort()
  return (
    firstKeys.length === secondKeys.length &&
    firstKeys.every(
      (key, index) => key === secondKeys[index] && insightValuesEqual(first[key], second[key]),
    )
  )
}

export function formatStructuredInsightValue(value: unknown, space = 2): string {
  try {
    return JSON.stringify(value, null, space) ?? String(value)
  } catch {
    return String(value)
  }
}
